import './component.scss'
import '@stimulus/polyfills'
import { Application, Controller } from 'stimulus'

const app = Application.start()

app.register('nurikae-site--pages--company--contacts--new--component', class extends Controller {
  connect() {
    this.setupGATracking()
    this.setupFormValidation()
  }

  setupGATracking() {
    // GAイベント送信処理
    const sendGaEvent = (event) => {
      const currentTarget = event.currentTarget
      const name = currentTarget?.name

      if (window && window.gtag && currentTarget && name) {
        // 計測用のイベント送信
        window.gtag('event', 'nomination_inquiry', {
          ga_event_category: name,
          ga_event_name: name,
          send_to: 'G-LW47TGHVE4',
        })

        // 同じname属性を持つ兄弟要素のイベントを解除
        const siblingsWithSameName = document.querySelectorAll(`[name="${name}"]`)
        siblingsWithSameName.forEach((element) => {
          element.removeEventListener(event.type, sendGaEvent)
        })
      }
    }

    // イベントハンドラーを設定
    const attachEventHandlers = (selectors, eventType) => {
      const elements = document.querySelectorAll(selectors)
      elements.forEach((element) => {
        element.addEventListener(eventType, sendGaEvent, { once: true }) // `once: true`で1回だけ実行
      })
    }

    // 各種イベントハンドラーを設定
    attachEventHandlers('input[type="text"], input[type="email"], input[type="tel"], textarea', 'input')
    attachEventHandlers('input[type="submit"], button', 'click')
    attachEventHandlers('input[type="radio"], input[type="checkbox"], select', 'change')
  }

  setupFormValidation() {
    // DOMが完全に読み込まれるまで待機
    const initValidation = () => {
      const form = document.getElementById('company-contact-form')
      if (!form) return

      // 送信ボタンはフォーム外にあるので、documentから直接取得
      const submitButton = document.querySelector('.company-contact__form-submit-button')
      if (!submitButton) return

      // 必須フィールドを取得（実際のname属性を確認）
      // form_withを使っているため、name属性にプレフィックスが付く可能性がある
      let fullNameInput = form.querySelector('input[name="extra_input_params[full_name]"]')
      if (!fullNameInput) {
        // 別のname属性パターンを試す
        fullNameInput = form.querySelector('input[name*="full_name"]')
      }

      let emailInput = form.querySelector('input[name="email"]')
      if (!emailInput) {
        emailInput = form.querySelector('input[name*="email"]')
      }

      let telInput = form.querySelector('input[name="tel"]')
      if (!telInput) {
        telInput = form.querySelector('input[name*="tel"]')
      }

      let checkboxes = form.querySelectorAll('input[name="extra_input_params[request_types][]"]')
      if (checkboxes.length === 0) {
        checkboxes = form.querySelectorAll('input[type="checkbox"][name*="request_types"]')
      }

      // 必須要素の存在確認
      if (!fullNameInput || !emailInput || !telInput || checkboxes.length === 0) {
        return
      }

      // エラー表示・非表示の関数
      const showError = (errorFor, errorType = null) => {
        let selector = `.company-contact__input-error[data-error-for="${errorFor}"]`
        if (errorType) {
          selector += `[data-error-type="${errorType}"]`
        }
        const errorElement = document.querySelector(selector)
        if (errorElement) {
          errorElement.style.display = 'flex'
        }
      }

      const hideError = (errorFor, errorType = null) => {
        let selector = `.company-contact__input-error[data-error-for="${errorFor}"]`
        if (errorType) {
          selector += `[data-error-type="${errorType}"]`
        }
        const errorElements = document.querySelectorAll(selector)
        errorElements.forEach(element => {
          element.style.display = 'none'
        })
      }

      const hideAllErrors = (errorFor) => {
        const errorElements = document.querySelectorAll(`.company-contact__input-error[data-error-for="${errorFor}"]`)
        errorElements.forEach(element => {
          element.style.display = 'none'
        })
      }

      // 各フィールドのバリデーション関数
      const validateFullName = () => {
        const value = fullNameInput.value.trim()
        if (value === '') {
          showError('full_name')
          return false
        } else {
          hideError('full_name')
          return true
        }
      }

      const validateEmail = () => {
        const value = emailInput.value.trim()
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        if (value === '') {
          hideAllErrors('email')
          showError('email', 'required')
          return false
        } else if (!emailRegex.test(value)) {
          hideAllErrors('email')
          showError('email', 'format')
          return false
        } else {
          hideAllErrors('email')
          return true
        }
      }

      const validateTel = () => {
        const value = telInput.value.trim()
        const telRegex = /^[0-9]{10,11}$/

        if (value === '') {
          hideAllErrors('tel')
          showError('tel', 'required')
          return false
        } else if (!telRegex.test(value)) {
          hideAllErrors('tel')
          showError('tel', 'format')
          return false
        } else {
          hideAllErrors('tel')
          return true
        }
      }

      const validateCheckboxes = () => {
        const isChecked = Array.from(checkboxes).some(checkbox => checkbox.checked)
        if (!isChecked) {
          showError('request_types')
          return false
        } else {
          hideError('request_types')
          return true
        }
      }

      // イベントリスナーを設定（個別バリデーションのみ）
      fullNameInput.addEventListener('input', validateFullName)
      fullNameInput.addEventListener('blur', validateFullName)

      emailInput.addEventListener('input', validateEmail)
      emailInput.addEventListener('blur', validateEmail)

      telInput.addEventListener('input', validateTel)
      telInput.addEventListener('blur', validateTel)

      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', validateCheckboxes)
      })

      // フォーム送信時のバリデーション
      submitButton.addEventListener('click', (event) => {
        // 全フィールドをバリデーション
        const isFullNameValid = validateFullName()
        const isEmailValid = validateEmail()
        const isTelValid = validateTel()
        const isCheckboxesValid = validateCheckboxes()

        // いずれかのバリデーションが失敗した場合は送信を阻止
        if (!isFullNameValid || !isEmailValid || !isTelValid || !isCheckboxesValid) {
          event.preventDefault()

          // 最初のエラー箇所にスクロール
          const firstError = document.querySelector('.company-contact__input-error[style*="display: flex"]')
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      })
    }

    // DOMの準備ができてから実行
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initValidation)
    } else {
      // すでに読み込み済みの場合は即座に実行
      initValidation()
    }
  }
})
